import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { EstadoVacio } from '../../components/ui/Estados';
import { repositorioSensores } from '../../../data/repositories/RepositorioSensores';
import { repositorioMedidores } from '../../../data/repositories/RepositorioMedidores';
import { repositorioActuadores } from '../../../data/repositories/RepositorioActuadores';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { ETIQUETA_ORIGEN, ORIGENES, type OrigenValor } from '../../../domain/entities/PanelPersonal';
import estilos from './Panel.module.css';

/** Una opción elegible, ya normalizada sea cual sea su origen. */
export interface Elegible {
  origen: OrigenValor;
  referencia: string;
  nombre: string;
  /** Dónde está o de qué depende: se enseña debajo del nombre. */
  detalle: string;
  unidad: string;
  tipo: string;
}

interface SelectorOrigenProps {
  abierto: boolean;
  onCerrar: () => void;
  onElegir: (elegido: Elegible) => void;
  /** Permite desvincular el elemento de lo que tuviera. */
  onQuitar?: () => void;
}

/**
 * Catálogo de todo lo que puede mostrar un marcador o una tarjeta.
 *
 * Empezó siendo sólo sensores. Un panel útil también quiere saber si un nodo
 * sigue vivo, cuánto consume un medidor o si una bomba está encendida, y esas
 * cuatro cosas viven en tablas distintas con identificadores distintos. Aquí se
 * presentan juntas y se devuelven en la misma forma, para que el panel no tenga
 * que saber de dónde salió cada una.
 *
 * Cada lista se pide sólo cuando se elige su pestaña: abrir el catálogo no debe
 * costar cuatro consultas cuando se va a usar una.
 */
export function SelectorOrigen({ abierto, onCerrar, onElegir, onQuitar }: SelectorOrigenProps) {
  const [origen, setOrigen] = useState<OrigenValor>('sensor');
  const [busqueda, setBusqueda] = useState('');

  const sensores = useQuery({
    queryKey: ['sensores', {}],
    queryFn: () => repositorioSensores.listarConValores({}),
    enabled: abierto && origen === 'sensor',
  });

  const nodos = useQuery({
    queryKey: ['catalogo', 'nodos', 'con-ubicacion'],
    queryFn: () => repositorioCatalogos.nodosConUbicacion(),
    enabled: abierto && origen === 'nodo',
    staleTime: 5 * 60_000,
  });

  const medidores = useQuery({
    queryKey: ['medidores', 'panel'],
    queryFn: () => repositorioMedidores.listar(),
    enabled: abierto && origen === 'medidor',
  });

  const actuadores = useQuery({
    queryKey: ['actuadores', 'panel'],
    queryFn: () => repositorioActuadores.listar({}),
    enabled: abierto && origen === 'actuador',
  });

  const opciones = useMemo<Elegible[]>(() => {
    if (origen === 'sensor') {
      return (sensores.data ?? []).map((s) => ({
        origen: 'sensor' as const,
        referencia: String(s.id),
        nombre: s.nombre,
        detalle: [s.areaNombre, s.nodoNombre].filter(Boolean).join(' · '),
        unidad: s.unidadMostrable,
        tipo: s.tipoNombre ?? '',
      }));
    }

    if (origen === 'nodo') {
      return (nodos.data ?? []).map((n) => ({
        origen: 'nodo' as const,
        referencia: String(n.id),
        nombre: n.nombre,
        detalle: n.ubicacion ?? '',
        unidad: '',
        tipo: 'Conexión',
      }));
    }

    if (origen === 'medidor') {
      return (medidores.data ?? []).map((m) => ({
        origen: 'medidor' as const,
        referencia: m.id,
        nombre: m.nombre,
        detalle: [m.clienteNombre, m.serial].filter(Boolean).join(' · '),
        unidad: 'W',
        tipo: 'Potencia total',
      }));
    }

    return (actuadores.data ?? []).map((a) => ({
      origen: 'actuador' as const,
      referencia: String(a.id),
      nombre: a.nombre,
      detalle: a.ubicacion,
      unidad: '',
      // Sin tipo: el valor de la tarjeta ya dice «Encendido» o «Apagado», y
      // repetirlo debajo gasta la línea que necesita la ubicación.
      tipo: '',
    }));
  }, [origen, sensores.data, nodos.data, medidores.data, actuadores.data]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = q
      ? opciones.filter((o) => `${o.nombre} ${o.detalle}`.toLowerCase().includes(q))
      : opciones;

    // Un catálogo con cientos de filas se navega buscando, no desplazándose.
    return lista.slice(0, 80);
  }, [opciones, busqueda]);

  const cargando =
    sensores.isFetching || nodos.isFetching || medidores.isFetching || actuadores.isFetching;

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Elegir qué se muestra"
      descripcion="El elemento enseñará su valor en vivo."
      pie={
        <>
          {onQuitar && (
            <Button variante="secundario" onClick={onQuitar}>
              Dejarlo vacío
            </Button>
          )}
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
        </>
      }
    >
      <Select
        etiqueta="Tipo"
        value={origen}
        onChange={(e) => {
          setOrigen(e.target.value as OrigenValor);
          // La búsqueda de un tipo no sirve para el siguiente.
          setBusqueda('');
        }}
        opciones={ORIGENES.map((o) => ({ valor: o, etiqueta: ETIQUETA_ORIGEN[o] }))}
      />

      <Input
        etiqueta="Buscar"
        icono="buscar"
        placeholder="Nombre, área o nodo"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
      />

      {filtrados.length === 0 ? (
        <EstadoVacio
          icono="buscar"
          titulo={cargando ? 'Cargando…' : 'Nada que elegir'}
          descripcion={
            cargando
              ? 'Un momento.'
              : busqueda
                ? 'Ninguno coincide con lo que buscas.'
                : 'No hay elementos de este tipo a tu alcance.'
          }
        />
      ) : (
        <div className={estilos.catalogoLista} role="list">
          {filtrados.map((o) => (
            <button
              key={`${o.origen}-${o.referencia}`}
              type="button"
              role="listitem"
              className={estilos.catalogoOpcion}
              onClick={() => onElegir(o)}
            >
              <span className={estilos.catalogoNombre}>{o.nombre}</span>
              <span className={estilos.catalogoDetalle}>
                {[o.tipo, o.detalle].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
